import { hasPermission } from '@nwm-oss/auth-react/permissions';
import { useFrontendAuth } from '@nwm-oss/auth-react/session';

// Enumerate every permission code defined in OSS_INTEGRATION_PERMISSIONS (prisma/seed.ts).
// flow-admin carries wildcards (flow.operations.*, integration.dlq.*) so hasPermission
// handles the short-circuit without a separate role check.
// DASHBOARD_VIEW / OWNER_ACCESS are retained for backwards-compatibility; they return false
// because the oss-integration custom auth model does not define those codes.
export type Permission =
  | 'flow.access'
  | 'flow.operations.execute'
  | 'flow.analytics.read'
  | 'integration.execution.retry'
  | 'integration.execution.replay'
  | 'integration.dlq.discard'
  | 'integration.dlq.requeue'
  | 'integration.payload.revealSensitive'
  | 'integration.partner.view'
  | 'integration.partner.create'
  | 'integration.partner.edit'
  | 'integration.partner.delete'
  | 'integration.partner.submit'
  | 'integration.partner.approve'
  | 'integration.partner.reject'
  | 'integration.partner.suspend'
  | 'integration.partner.reactivate'
  | 'integration.partner.rollback'
  | 'integration.partner.connectivity.test'
  | 'integration.partner.audit.view'
  | 'DASHBOARD_VIEW'
  | 'OWNER_ACCESS';

/**
 * Check if the current user has a permission.
 *
 * @param permission - The permission code to check.
 * @param orgId - Optional org context guard. If provided, returns false when the
 *   resolved permissions belong to a different org (prevents cross-org reuse on
 *   components that are tied to a specific tenant context).
 */
export function usePermission(permission: Permission, orgId?: string): boolean {
  const { identity } = useFrontendAuth();
  if (!identity) return false;
  if (orgId !== undefined && identity.activeOrgId !== orgId) return false;
  return hasPermission(identity.permissions, permission);
}

// AS2 certificate and transport-profile management have no dedicated permission codes in
// OSS_INTEGRATION_PERMISSIONS (prisma/seed.ts) — the backend gates them by role instead
// (requireFlowAdmin(), apps/integration/backend/src/common/auth/flow-authorization.ts:4,42),
// checking request.auth.roles against FLOW_ADMIN_ROLE_CODES = ['flow-admin', 'oss.flow.admin'].
// This mirrors that exact check for frontend defense-in-depth gating on those two pages.
const FLOW_ADMIN_ROLE_CODES = new Set(['flow-admin', 'oss.flow.admin']);

export function useIsFlowAdmin(): boolean {
  const { identity } = useFrontendAuth();
  if (!identity) return false;
  return identity.roles.some((role) => FLOW_ADMIN_ROLE_CODES.has(role.trim().toLowerCase()));
}
